package nl.volleyplanner.extensions

import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toInstant
import kotlin.time.Duration
import kotlin.time.ExperimentalTime

/**
 * Checks if this range overlaps with another range
 */
fun <T : Comparable<T>> ClosedRange<T>.overlaps(other: ClosedRange<T>?): Boolean {
    if (other == null) return false
    return start < other.endInclusive && other.start < endInclusive
}

/**
 * Checks if this range overlaps with another range
 */
fun <T : Comparable<T>> OpenEndRange<T>.overlaps(other: OpenEndRange<T>?): Boolean {
    if (other == null) return false
    return start < other.endExclusive && other.start < endExclusive
}

/**
 * Returns the duration until the other range, or zero if the ranges overlap.
 */
@OptIn(ExperimentalTime::class)
fun ClosedRange<LocalDateTime>.durationUntil(other: ClosedRange<LocalDateTime>?, timeZone: TimeZone = DEFAULT_TIMEZONE) : Duration? {
    if (other == null) return null
    if (overlaps(other)) return Duration.ZERO

    return if (other.endInclusive <= start) {
        start.toInstant(timeZone) - other.endInclusive.toInstant(timeZone)
    } else {
        other.start.toInstant(timeZone) - endInclusive.toInstant(timeZone)
    }
}

/**
 * Returns the duration until the other range, or zero if the ranges overlap.
 * Note that for this function endExclusive is actually treated as it is unknown
 * which time component to really take into consideration to exclude
 */
@OptIn(ExperimentalTime::class)
fun OpenEndRange<LocalDateTime>.durationUntil(other: OpenEndRange<LocalDateTime>?, timeZone: TimeZone = DEFAULT_TIMEZONE) : Duration? {
    if (other == null) return null
    if (overlaps(other)) return Duration.ZERO

    return if (other.endExclusive <= start) {
        start.toInstant(timeZone) - other.endExclusive.toInstant(timeZone)
    } else {
        other.start.toInstant(timeZone) - endExclusive.toInstant(timeZone)
    }
}

typealias LocalDateTimeRange = ClosedRange<LocalDateTime>
typealias LocalDateTimeOpenRange = OpenEndRange<LocalDateTime>